import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  BarChart2,
  Calendar,
  PieChart,
  List,
  Activity,
  Brain,
  Award,
  Search,
  BookOpen,
  Grid,
  Zap,
  Radio,
  Copy,
  Check,
  ChevronRight,
  Lock,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';

// ---------------------------------------------------------------------------
// Static tool list (13 tools)
// ---------------------------------------------------------------------------
const MCP_TOOLS = [
  { name: 'get_market_data',        desc: 'Real-time quote for any symbol',           icon: TrendingUp },
  { name: 'get_historical_data',    desc: 'OHLCV bars — daily, weekly, monthly',      icon: BarChart2 },
  { name: 'get_economic_calendar',  desc: 'Upcoming macro events & impact ratings',   icon: Calendar },
  { name: 'get_portfolio_summary',  desc: 'Current portfolio value & P&L snapshot',   icon: PieChart },
  { name: 'list_portfolio_holdings', desc: 'All positions with entry price & P&L',    icon: List },
  { name: 'list_trades',            desc: 'Trade history with outcomes',              icon: Activity },
  { name: 'list_trading_analyses',  desc: 'AI signal history for any symbol',         icon: Brain },
  { name: 'get_trading_performance', desc: 'Win rate, drawdown, profit factor',       icon: Award },
  { name: 'search_strategies',      desc: 'Query your saved trading strategies',      icon: Search },
  { name: 'list_strategies',        desc: 'All strategies with rules & parameters',   icon: BookOpen },
  { name: 'get_market_heatmap',     desc: 'Sector performance across JSE & US',       icon: Grid },
  { name: 'get_hot_movers',         desc: 'Top gainers & losers right now',           icon: Zap },
  { name: 'list_signals',           desc: 'Latest AI-generated trading signals',      icon: Radio },
];

// ---------------------------------------------------------------------------
// Config snippet
// ---------------------------------------------------------------------------
const MCP_SSE_URL = 'https://invest.marome-investments.co.za/mcp/sse';

function buildSnippet(apiKey: string) {
  return JSON.stringify({
    mcpServers: {
      marome: {
        url: MCP_SSE_URL,
        headers: { Authorization: `Bearer ${apiKey}` },
      },
    },
  }, null, 2);
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
    >
      {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function SetupGuide({ isProUser }: { isProUser: boolean }) {
  const [activeTab, setActiveTab] = useState<'claude' | 'cursor' | 'claude-code'>('claude');
  const placeholderKey = 'YOUR_MCP_API_KEY';
  const snippet = buildSnippet(placeholderKey);

  const tabs = [
    { id: 'claude' as const, label: 'Claude Desktop', hint: 'Add to claude_desktop_config.json' },
    { id: 'cursor' as const, label: 'Cursor', hint: 'Add to .cursor/mcp.json in project root' },
    { id: 'claude-code' as const, label: 'Claude Code', hint: 'Add to .claude/mcp.json or use /mcp add' },
  ];

  return (
    <div className="bg-slate-900/60 border border-slate-700 rounded-2xl p-6 md:p-8">
      <h3 className="text-xl font-bold text-white mb-2">Quick Setup</h3>
      <p className="text-slate-400 text-sm mb-6">
        Choose your AI tool and paste the config. Replace <code className="text-cyan-400">YOUR_MCP_API_KEY</code> with your actual key.
      </p>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
              activeTab === t.id
                ? 'bg-cyan-600 text-white font-medium'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Config block */}
      <div className="relative bg-slate-950 border border-slate-700 rounded-xl p-5">
        <div className="absolute top-4 right-4">
          <CopyButton text={snippet} />
        </div>
        <pre className="text-xs text-slate-300 overflow-x-auto pr-16 leading-relaxed">{snippet}</pre>
        <p className="text-xs text-slate-500 mt-3 border-t border-slate-800 pt-3">
          {tabs.find(t => t.id === activeTab)?.hint}
        </p>
      </div>

      {!isProUser && (
        <div className="mt-4 flex items-center gap-2 text-xs text-amber-400/80">
          <Lock size={12} />
          Generate your API key after upgrading to Pro or Premium.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const McpPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { isPro, isPremium } = useSubscription();
  const hasAccess = isPro || isPremium;


  // Dynamic CTA
  let ctaText = 'Start Free Trial';
  let ctaTo = '/register';
  if (isAuthenticated && hasAccess) {
    ctaText = 'Get Your API Key';
    ctaTo = '/settings/mcp';
  } else if (isAuthenticated && !hasAccess) {
    ctaText = 'Upgrade to Pro';
    ctaTo = '/billing';
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEO
        title="MCP Integration"
        description="Connect My Investments to your AI assistant via the Model Context Protocol (MCP) for real-time market data and trading signal access."
        canonical="https://my-investments.io/mcp"
      />

      {/* ── Hero ── */}
      <section className="relative py-24 md:py-36 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/10 to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative">
          <p className="text-xs font-semibold tracking-widest uppercase text-cyan-400/70 mb-4">
            Model Context Protocol
          </p>
          <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-[1.05] mb-6">
            Connect your AI to
            <br />
            <span className="text-cyan-400">your portfolio</span>
          </h1>
          <p className="text-lg text-slate-400 leading-relaxed mb-10 max-w-2xl mx-auto">
            13 my investments trading tools exposed via MCP. Ask Claude about your holdings, signals, and market data
            — directly from Claude Desktop, Cursor, or Claude Code.
          </p>
          <Link
            to={ctaTo}
            className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-semibold text-sm transition-colors"
          >
            {ctaText}
            <ChevronRight size={16} />
          </Link>
        </div>
      </section>

      {/* ── Tool showcase ── */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-3">13 tools at your fingertips</h2>
          <p className="text-slate-400 text-center text-sm mb-10">
            All 13 tools are available equally to Pro and Premium subscribers.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MCP_TOOLS.map((tool) => (
              <div
                key={tool.name}
                className="flex items-start gap-3 bg-slate-900/60 border border-slate-800 rounded-xl p-4 hover:border-slate-600 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <tool.icon size={16} className="text-cyan-400" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-slate-200">{tool.name}</code>
                    <span className="text-[10px] bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded px-1.5 py-0.5 font-medium">
                      Pro+
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-snug">{tool.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Setup guide ── */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <SetupGuide isProUser={isAuthenticated && hasAccess} />
        </div>
      </section>

      {/* ── Plan comparison ── */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-10">What each plan unlocks</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              {
                tier: 'Free',
                color: 'slate',
                features: [
                  '✗ No MCP access',
                  '✗ No API keys',
                  '✗ No portfolio tools',
                ],
              },
              {
                tier: 'Pro & Premium',
                color: 'cyan',
                highlight: true,
                features: [
                  'All 13 MCP tools',
                  'Portfolio & holdings',
                  'Trade history & P&L',
                  'AI signals & analyses',
                  'Strategy library',
                  'Up to 5 API keys',
                ],
              },
            ].map((plan) => (
              <div
                key={plan.tier}
                className={`rounded-2xl p-6 border ${
                  plan.highlight
                    ? 'bg-cyan-900/20 border-cyan-600/40'
                    : 'bg-slate-900/40 border-slate-700'
                }`}
              >
                <h3 className={`text-lg font-bold mb-4 ${plan.highlight ? 'text-cyan-400' : 'text-slate-200'}`}>
                  {plan.tier}
                </h3>
                <ul className="space-y-2">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className={`text-sm flex items-center gap-2 ${
                        f.startsWith('✗') ? 'text-slate-600' : 'text-slate-300'
                      }`}
                    >
                      {f.startsWith('✗') ? (
                        <span className="text-slate-700">✗</span>
                      ) : (
                        <Check size={13} className={plan.highlight ? 'text-cyan-400' : 'text-slate-500'} />
                      )}
                      {f.replace('✗ ', '')}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer links ── */}
      <section className="py-12 px-6 border-t border-slate-800/60">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-slate-500 text-sm mb-4">Need more detail?</p>
          <div className="flex flex-wrap justify-center gap-4">
            {isAuthenticated ? (
              <Link
                to="/knowledge/articles/mcp-integration-guide"
                className="inline-flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                Detailed technical reference <ExternalLink size={13} />
              </Link>
            ) : (
              <Link
                to="/knowledge"
                className="inline-flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                Knowledge Base <ExternalLink size={13} />
              </Link>
            )}
            <Link
              to={ctaTo}
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
            >
              {ctaText} <ChevronRight size={13} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default McpPage;
